import React, { Fragment } from "react";
import { getUserProfile, ping } from "./actions/authentication";
import {
    getAlerts,
    getPortalConfigFlags,
    getPythonIntegrations,
} from "./actions/config";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { Route, Switch, useLocation } from "react-router-dom";
import {
    Container,
    styled,
    CssBaseline,
    CircularProgress,
    Paper,
} from "@mui/material";
import { hexToRgb } from "./components/common/utils/utils";
import clsx from "clsx";
import SideBar from "./components/SideBar";
import { RoutesAndLinks } from "./typeDefs/common";
import "./App.css";
import ImageBackdrop from "./components/common/utils/ImageBackdrop";
import {
    checkIfYardiDbExists,
    checkYardiDbStatus,
    getIsLockedDown,
    getYardiDbs,
} from "./actions/yardidata";
import Message from "./components/common/utils/Message";
import { useTheme } from "@mui/material/styles";
import useCurrentWidth from "./components/common/utils/useCurrentWidth";
import { closeSidebar, openSidebar } from "./reducers/common";
import Header from "./components/common/utils/Header";
import { getIntegrations } from "./actions/integrations";
import NotFound from "./components/common/NotFound";
import AlertsDrawer from "./components/common/AlertsDrawer";
import { setErrorMessage, setSuccessMessage } from "./actions/common";
import { getTemplates } from "./actions/templates";

const drawerWidth = 250;

const roleClaimType =
    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";

type Role = "33fadmin" | "admin" | "user";

const LoadingGif = styled("img")(() => ({
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    height: "150px",
}));

const LoadingBackground = styled("div")(({ theme }) => ({
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(337deg, ${
        theme.palette.gradient!.light
    } 6.05%, ${theme.palette.gradient!.main} 34.44%, ${
        theme.palette.gradient!.dark
    } 82.01%);`,
    ".MuiPaper-root": {
        position: "absolute",
        top: "50px",
        left: "50%",
        transform: "translateX(-50%)",
        padding: 8,
        borderRadius: "30px",
        boxShadow: theme.shadows[5],
    },
}));

const Root = styled("div", {
    shouldForwardProp: (prop) => prop !== "darkBackground",
})<{
    darkBackground: boolean;
}>(({ darkBackground }) => ({
    display: "flex",
    minHeight: "100vh",
    background: darkBackground ? "#F5F5F7" : "white",
}));

const Content = styled("main", {
    shouldForwardProp: (prop) => prop !== "sidebarOpen",
})<{
    sidebarOpen?: boolean;
}>(({ theme, sidebarOpen = true }) => ({
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    padding: 0,
    position: "relative",
    transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    maxWidth: sidebarOpen ? `calc(100vw - 248px)` : `calc(100vw - 63px)`,
}));

const App = (): JSX.Element => {
    const dispatch = useAppDispatch();

    const alerts = useAppSelector((state) => state.config.alerts);
    const { sidebarOpen } = useAppSelector((state) => state.common);

    const role: Role = useAppSelector(
        (state) => state.authentication.user[roleClaimType]
    );
    const needsCreds = useAppSelector((state) => state.yardidata.needsCreds);
    const pythonIntegrations = useAppSelector(
        (state) => state.config.pythonIntegrations
    );

    // const [displayBackground, setDisplayBackground] = React.useState(true);
    const [routesAndLinks, setRoutesAndLinks] = React.useState<RoutesAndLinks>({
        sideBarLinks: [],
        routes: [],
    });
    const [checkingCreds, setCheckingCreds] = React.useState<boolean>(true);

    const currentWidth = useCurrentWidth();

    const theme = useTheme();

    const pathname = useLocation().pathname;

    const darkBackgroundRoutes = [
        "/templates",
        "/integrations",
        "/documentation",
    ];

    // const location = useLocation();

    // React.useEffect(() => {
    //     if (
    //         location.pathname.length > 1 &&
    //         hideBackgroundRoutes.filter((r) => r.startsWith(location.pathname))
    //             .length > 0
    //     ) {
    //         setDisplayBackground(false);
    //     } else {
    //         setDisplayBackground(true);
    //     }
    // }, [location.pathname]);

    const getRoutesAndLinks = () => {
        if (!role || role.length === 0) return;

        if (role === "33fadmin") {
            import("./getAdminRoutes").then((f) => {
                const newRoutesAndLinks = f.default(needsCreds);
                setRoutesAndLinks(newRoutesAndLinks);
            });
        } else {
            import("./getClientRoutes").then((f) => {
                const newRoutesAndLinks = f.default(role, needsCreds);
                setRoutesAndLinks(newRoutesAndLinks);
            });
        }
    };

    React.useEffect(() => {
        dispatch(getUserProfile());
        dispatch(getPortalConfigFlags());
        dispatch(getPythonIntegrations());
        dispatch(getIntegrations());
        dispatch(getTemplates());
        if (role !== "33fadmin") {
            dispatch(getAlerts());
        }

        // const interval = setInterval(() => {
        //     ping();
        // }, 30000);
        // return () => clearInterval(interval);
    }, [dispatch]);

    React.useEffect(() => {
        if (role !== "33fadmin" && alerts.length > 0) {
            dispatch(
                setErrorMessage(
                    `You have ${alerts.length} new alert${
                        alerts.length === 1 ? "" : "s"
                    }.`,
                    alerts.length === 1 ? alerts[0].LinkTo : undefined
                )
            );
        }
    }, [role, alerts, dispatch]);

    React.useEffect(() => {
        if (["admin", "33fadmin", "user"].includes(role)) {
            dispatch(getYardiDbs());
            dispatch(checkIfYardiDbExists());
            dispatch(getIsLockedDown());
        }
    }, [role, dispatch]);

    React.useEffect(() => {
        getRoutesAndLinks();
    }, [role, needsCreds, pythonIntegrations]);

    React.useEffect(() => {
        if (currentWidth && currentWidth < theme.breakpoints.values.xl) {
            dispatch(closeSidebar());
        } else {
            dispatch(openSidebar());
        }
    }, [currentWidth]);

    React.useEffect(() => {
        if (routesAndLinks.routes.length > 0) setCheckingCreds(false);
    }, [routesAndLinks]);

    return (
        <Root
            darkBackground={darkBackgroundRoutes.some((r) =>
                pathname.startsWith(r)
            )}
        >
            {checkingCreds ? (
                <LoadingBackground>
                    <LoadingGif
                        src={`${window.location.origin}/images/DF-white-circle-load.gif`}
                        alt="loading gif"
                    />
                </LoadingBackground>
            ) : (
                <Fragment>
                    <CssBaseline />
                    <SideBar
                        drawerWidth={drawerWidth}
                        links={routesAndLinks.sideBarLinks.sort(
                            (a, b) => a.order - b.order
                        )}
                    />
                    <Content sidebarOpen={sidebarOpen}>
                        {role !== "33fadmin" && (
                            <AlertsDrawer drawerWidth={drawerWidth} />
                        )}
                        <Header />
                        <Switch>{routesAndLinks.routes}</Switch>
                        <ImageBackdrop />
                        <Message />
                    </Content>
                </Fragment>
            )}
        </Root>
    );
};

export default App;
